import { Box, Checkbox, FormControlLabel, FormGroup, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { useTranslation } from 'react-i18next'

import { modeHasCompatibleGranular } from '@/utils/testPromptAnswerMatrix'
import type { AnswerFormatMode } from '@/utils/testPromptAnswerMatrix'
import { TestCreateSection } from '../TestCreateSection'

/**
 * Формат ответа ученика и переключатель автопроверки.
 *
 * Недоступный формат не прячется, а гасится с подсказкой: иначе учитель не
 * понимает, почему нужного варианта нет — потому ли, что его запрещает матрица
 * отображений, или потому что он выключил автопроверку.
 */
export function TestAnswerFormatSection({
  modes,
  compatibleAnswerTypes,
  selectedModes,
  onSelectedModesChange,
  autoCheckEnabled,
  onAutoCheckEnabledChange,
  canDisableAutoCheck,
  idPrefix,
  hint,
}: {
  /** Форматы, которые предлагает эта форма (профиль конструктора). */
  modes: AnswerFormatMode[]
  /** Гранулярные форматы, допустимые матрицей для выбранных отображений. */
  compatibleAnswerTypes: string[]
  selectedModes: AnswerFormatMode[]
  onSelectedModesChange: (update: (prev: AnswerFormatMode[]) => AnswerFormatMode[]) => void
  autoCheckEnabled: boolean
  /** Нужен только форме с выключателем автопроверки. */
  onAutoCheckEnabledChange?: (value: boolean) => void
  /** Форма без выключателя держит автопроверку включённой. */
  canDisableAutoCheck: boolean
  idPrefix: string
  /**
   * Пояснение для формы без выключателя автопроверки: встроенные подсказки
   * ссылаются на выключатель, которого там нет.
   */
  hint?: string
}) {
  const { t } = useTranslation()

  return (
    <TestCreateSection
      id="answer-format"
      icon={<ChatBubbleOutlineIcon fontSize="small" />}
      title={t('tests.createForm.sectionAnswerFormat')}
    >
      {compatibleAnswerTypes.length > 0 ? (
        <FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
            {canDisableAutoCheck
              ? t(
                  autoCheckEnabled
                    ? 'tests.createForm.answerFormatModesHintAutoOn'
                    : 'tests.createForm.answerFormatModesHintAutoOff',
                )
              : hint}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            {modes.map((mode) => {
              const matrixAllows = modeHasCompatibleGranular(mode, compatibleAnswerTypes)
              // Без автопроверки остаётся только речь: печать и выбор варианта
              // проверять некому.
              const available = autoCheckEnabled ? matrixAllows : mode === 'speech' && matrixAllows
              const checked = selectedModes.includes(mode)
              const inputId = `${idPrefix}-answer-mode-${mode}`
              const toggleMode = () => {
                if (!available) return
                onSelectedModesChange((prev) =>
                  prev.includes(mode) ? prev.filter((x) => x !== mode) : [...prev, mode],
                )
              }
              const card = (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    overflow: 'hidden',
                    borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                    bgcolor: checked
                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22)
                      : theme.palette.background.paper,
                    opacity: available ? 1 : 0.55,
                    transition: theme.transitions.create(['background-color', 'border-color', 'opacity'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                  })}
                >
                  <Box
                    component="label"
                    htmlFor={inputId}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      p: 1.25,
                      m: 0,
                      cursor: available ? 'pointer' : 'not-allowed',
                      userSelect: 'none',
                    }}
                  >
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      disabled={!available}
                      onChange={toggleMode}
                      color="primary"
                      sx={{
                        p: 0,
                        mt: 0.125,
                        color: (theme) => (checked ? theme.palette.primary.main : theme.palette.action.active),
                        '&.Mui-checked': {
                          color: (theme) => theme.palette.primary.main,
                        },
                      }}
                    />
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          fontWeight: 600,
                          color: checked
                            ? theme.palette.mode === 'light'
                              ? theme.palette.primary.dark
                              : theme.palette.primary.light
                            : theme.palette.text.primary,
                        })}
                      >
                        {t(`tests.createForm.answerFormatModes.${mode}.title`)}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={(theme) => ({
                          display: 'block',
                          lineHeight: 1.35,
                          color: checked
                            ? theme.palette.mode === 'light'
                              ? alpha(theme.palette.primary.dark, 0.92)
                              : alpha(theme.palette.primary.light, 0.9)
                            : theme.palette.text.secondary,
                        })}
                      >
                        {t(`tests.createForm.answerFormatModes.${mode}.description`)}
                      </Typography>
                    </Stack>
                  </Box>
                </Paper>
              )
              const tooltipTitle = !matrixAllows
                ? t('tests.createForm.answerFormatModeUnavailable')
                : !autoCheckEnabled && mode !== 'speech'
                  ? t('tests.createForm.answerFormatTypedChooseRequireAutoCheck')
                  : ''
              return (
                <Box key={mode} sx={{ width: '100%', minWidth: 0 }}>
                  {available ? (
                    card
                  ) : (
                    <Tooltip title={tooltipTitle} arrow>
                      <Box component="span" sx={{ display: 'block', width: '100%' }}>
                        {card}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )
            })}
          </Box>
        </FormGroup>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('tests.form.answerFormatUnavailableHint')}
        </Typography>
      )}
      {canDisableAutoCheck ? (
        <FormControlLabel
          control={
            <Switch checked={autoCheckEnabled} onChange={(e) => onAutoCheckEnabledChange?.(e.target.checked)} />
          }
          label={t('tests.form.autoCheckEnabled')}
        />
      ) : null}
    </TestCreateSection>
  )
}
